import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  PiggyBank,
  ArrowUpRight,
  ArrowDownRight,
  Receipt,
  RefreshCw,
  Wallet,
  Sparkles,
  Film,
  Calculator,
  BarChart3,
  CreditCard,
  Megaphone,
  GitBranch,
  Briefcase,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { UsageDashboard } from './UsageDashboard'
import { FalDashboard } from './FalDashboard'
import { RevenueVsCostsChart } from './RevenueVsCostsChart'
import { UnitEconomicsDashboard } from './UnitEconomicsDashboard'
import AdminDashboardPage from '@/routes/admin/index'
import AdminRampPage from '@/routes/admin/ramp/index'
import PaidAttributionPage from '@/routes/admin/attribution/paid/index'
import OrganicAttributionPage from '@/routes/admin/attribution/organic/index'
import AdminInvestorsPage from '@/routes/admin/investors/index'

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

function centsToUsd(cents: number): number {
  return cents / 100
}

export default function ProfitLossPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'overall'
  const [monthOffset, setMonthOffset] = useState(0)

  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  const costData = useQuery(api.costTracking.getCostDashboard)
  const refreshCosts = useAction(api.costTrackingActions.refreshCosts)
  const [refreshing, setRefreshing] = useState(false)

  const hasData = costData != null

  const fetchRampMtdSpend = useAction(api.ramp.getMonthToDateSpendByMerchant)
  const [rampMtd, setRampMtd] = useState<{ convex: number; other: number } | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchRampMtdSpend({})
      .then((res) => {
        if (cancelled) return
        const byMerchant = (res as { byMerchant?: Record<string, number> }).byMerchant ?? {}
        const TRACKED_SUBSTRINGS = [
          'convex',
          'anthropic',
          'fal',
          'bunny',
          'google',
          'amazon',
          'aws',
          'railway',
        ]
        let convex = 0
        let other = 0
        for (const [name, amount] of Object.entries(byMerchant)) {
          const lower = name.toLowerCase()
          if (lower.includes('convex')) {
            convex += amount
          } else if (!TRACKED_SUBSTRINGS.some((s) => lower.includes(s))) {
            other += amount
          }
        }
        setRampMtd({ convex, other })
      })
      .catch((err) => {
        console.error('[P&L] Failed to fetch Ramp MTD spend:', err)
      })
    return () => {
      cancelled = true
    }
  }, [fetchRampMtdSpend])

  const dayOfMonth = new Date().getDate()
  const railwayEstimate = dayOfMonth * 3
  const rampSpend = rampMtd ? rampMtd.convex + rampMtd.other : 0

  const isLoading = costData === undefined || rampMtd === null

  // Variable cost = customer-driven LLM + all other vendor cost. Fixed is shown
  // separately so the customer-attributable spend isn't inflated by evals/admin.
  const variableLlmCents = hasData
    ? (costData.currentMonth.anthropic_variable ?? costData.currentMonth.anthropic) +
      (costData.currentMonth.google_gemini_variable ?? costData.currentMonth.google_gemini ?? 0)
    : 0
  const fixedLlmCents = hasData ? (costData.currentMonth.fixed_total ?? 0) : 0

  const variableNonLlmCents = hasData
    ? costData.currentMonth.fal + costData.currentMonth.bunny + costData.currentMonth.google_ads + (costData.currentMonth.aws ?? 0)
    : 0
  const variableCosts = centsToUsd(variableLlmCents + variableNonLlmCents) + rampSpend + railwayEstimate
  const fixedCosts = centsToUsd(fixedLlmCents)
  const totalCosts = variableCosts + fixedCosts
  const totalRevenue = hasData ? centsToUsd(costData.revenue ?? 0) : 0
  const netMargin = totalRevenue - variableCosts // Margin computed on variable cost (default view)

  const costCategories = hasData && rampMtd
    ? [
        {
          name: 'Anthropic',
          amount: centsToUsd(costData.currentMonth.anthropic_variable ?? costData.currentMonth.anthropic),
          estimated: false,
        },
        {
          name: 'Fal.ai (Video Generation)',
          amount: centsToUsd(costData.currentMonth.fal),
          estimated: false,
        },
        {
          name: 'BunnyCDN (Storage and Content Delivery)',
          amount: centsToUsd(costData.currentMonth.bunny),
          estimated: false,
        },
        {
          name: 'Google Ads',
          amount: centsToUsd(costData.currentMonth.google_ads),
          estimated: false,
        },
        {
          name: 'Google (Gemini API)',
          amount: centsToUsd(costData.currentMonth.google_gemini_variable ?? costData.currentMonth.google_gemini ?? 0),
          estimated: false,
        },
        {
          name: 'AWS (Remotion & GPU Rendering)',
          amount: centsToUsd(costData.currentMonth.aws ?? 0),
          estimated: false,
        },
        {
          name: 'Convex (Database)',
          amount: rampMtd.convex,
          estimated: false,
        },
        {
          name: 'Other (Ramp)',
          amount: rampMtd.other,
          estimated: false,
        },
        {
          name: 'Railway (Hosting)',
          amount: railwayEstimate,
          estimated: true,
        },
      ].sort((a, b) => b.amount - a.amount)
    : []

  const fixedCostCategories = hasData
    ? [
        {
          name: 'Anthropic — Evals',
          amount: centsToUsd(costData.currentMonth.anthropic_fixed_eval ?? 0),
        },
        {
          name: 'Anthropic — Admin / Team',
          amount: centsToUsd(costData.currentMonth.anthropic_fixed_admin ?? 0),
        },
        {
          name: 'Gemini — Internal',
          amount: centsToUsd(costData.currentMonth.google_gemini_fixed ?? 0),
        },
      ].filter((c) => c.amount > 0).sort((a, b) => b.amount - a.amount)
    : []

  const barColor = 'bg-purple-500'

  const tabTriggerClass = "relative px-5 py-3 text-sm font-medium text-neutral-400 hover:text-neutral-600 hover:bg-neutral-200/60 transition-colors rounded-none rounded-t-lg shadow-none bg-transparent data-[state=active]:bg-neutral-50 data-[state=active]:text-neutral-900 data-[state=active]:shadow-none data-[state=active]:border data-[state=active]:border-neutral-400 data-[state=active]:border-b-neutral-50 data-[state=active]:-mb-px gap-2"

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Business Metrics</h1>
          <p className="text-sm text-neutral-500">
            P&L, dashboard, ad spend, card spend, LLM usage, and unit economics
          </p>
        </div>
        {activeTab === 'overall' && (
          <span className="text-xl font-bold text-blue-900 border border-blue-200 px-4 py-2 rounded-md bg-blue-50">
            {currentMonth}
          </span>
        )}
      </div>

      {activeTab === 'overall' && hasData && costData.lastUpdated && (
        <p className="text-xs text-neutral-400 flex items-center gap-1.5 mb-4">
          Last updated: {new Date(costData.lastUpdated).toLocaleString()}
          <button
            onClick={() => {
              setRefreshing(true)
              refreshCosts()
                .catch((err) => console.error('[P&L] Refresh failed:', err))
                .finally(() => setRefreshing(false))
            }}
            disabled={refreshing}
            className="inline-flex items-center justify-center hover:text-neutral-600 disabled:opacity-50"
          >
            <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} />
          </button>
        </p>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => setSearchParams({ tab: value })}
        className="flex-1 flex flex-col min-h-0"
      >
        <TabsList className="w-full h-auto p-0 bg-neutral-50 rounded-none border-b border-neutral-400 justify-start gap-0 mb-6">
          <TabsTrigger value="overall" className={tabTriggerClass}>
            <Wallet className="h-4 w-4" />
            Overall
          </TabsTrigger>
          <TabsTrigger value="dashboard" className={tabTriggerClass}>
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="ramp" className={tabTriggerClass}>
            <CreditCard className="h-4 w-4" />
            Ramp
          </TabsTrigger>
          <TabsTrigger value="agent" className={tabTriggerClass}>
            <Sparkles className="h-4 w-4" />
            LLM Usage
          </TabsTrigger>
          <TabsTrigger value="fal" className={tabTriggerClass}>
            <Film className="h-4 w-4" />
            Fal.ai
          </TabsTrigger>
          <TabsTrigger value="unit-economics" className={tabTriggerClass}>
            <Calculator className="h-4 w-4" />
            Unit Economics
          </TabsTrigger>
          <TabsTrigger value="paid-attribution" className={tabTriggerClass}>
            <Megaphone className="h-4 w-4" />
            Paid Attribution
          </TabsTrigger>
          <TabsTrigger value="organic-attribution" className={tabTriggerClass}>
            <GitBranch className="h-4 w-4" />
            Organic Attribution
          </TabsTrigger>
          <TabsTrigger value="investors" className={tabTriggerClass}>
            <Briefcase className="h-4 w-4" />
            Investors
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overall" className="flex-1 min-h-0 mt-0">
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-neutral-500">Month-to-date Revenue</p>
                      <p className={cn(
                        'text-2xl font-bold',
                        totalRevenue > 0 ? 'text-green-600' : 'text-neutral-900'
                      )}>
                        {isLoading ? '...' : formatCurrency(totalRevenue)}
                      </p>
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-green-300" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-neutral-500">Month-to-date Costs</p>
                      <p className="text-2xl font-bold text-neutral-900">
                        {isLoading ? '...' : formatCurrency(totalCosts)}
                      </p>
                    </div>
                    <ArrowDownRight className="h-5 w-5 text-red-300" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-neutral-500">Net Profit</p>
                      <p className={cn(
                        'text-2xl font-bold',
                        netMargin >= 0 ? 'text-green-600' : 'text-red-600'
                      )}>
                        {isLoading ? '...' : formatCurrency(netMargin)}
                      </p>
                    </div>
                    <PiggyBank className={cn('h-8 w-8', netMargin >= 0 ? 'text-green-200' : 'text-red-200')} />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cost Breakdown */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Cost Breakdown
                  </CardTitle>
                  <CardDescription>Month-to-date costs by provider</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-neutral-300">Loading...</span>
                            <span className="text-sm text-neutral-300">--</span>
                          </div>
                          <div className="h-2 bg-neutral-100 rounded-full" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {costCategories.map((category, index) => (
                        <div key={index}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-neutral-700">{category.name}</span>
                            <span className="text-sm font-medium text-neutral-900">
                              {category.estimated && (
                                <span className="text-xs text-neutral-400 mr-1">(Estimated)</span>
                              )}
                              {formatCurrency(category.amount)}
                            </span>
                          </div>
                          <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                            <div
                              className={cn('h-full rounded-full', barColor)}
                              style={{ width: `${totalCosts > 0 ? Math.min((category.amount / totalCosts) * 100, 100) : 0}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-6 pt-4 border-t border-neutral-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-neutral-700">Variable Subtotal</span>
                      <span className="font-medium text-neutral-900">
                        {isLoading ? '...' : formatCurrency(variableCosts)}
                      </span>
                    </div>
                    {fixedCostCategories.length > 0 && (
                      <>
                        <div className="mt-3 mb-1 text-[11px] font-medium text-neutral-400 uppercase tracking-wide">
                          Fixed Costs (Excluded from variable P&amp;L)
                        </div>
                        <div className="space-y-1.5">
                          {fixedCostCategories.map((category, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                              <span className="text-neutral-600">{category.name}</span>
                              <span className="text-neutral-700">{formatCurrency(category.amount)}</span>
                            </div>
                          ))}
                          <div className="flex items-center justify-between text-sm pt-1 border-t border-neutral-100">
                            <span className="font-medium text-neutral-700">Fixed Subtotal</span>
                            <span className="font-medium text-neutral-900">{formatCurrency(fixedCosts)}</span>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="mt-3 pt-3 border-t border-neutral-200 flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">Total Costs ({currentMonth})</span>
                      <span className="text-sm font-bold text-neutral-900">
                        {isLoading ? '...' : formatCurrency(totalCosts)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <RevenueVsCostsChart />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="dashboard" className="flex-1 min-h-0 mt-0 -mx-6 -mb-6">
          <AdminDashboardPage />
        </TabsContent>

        <TabsContent value="ramp" className="flex-1 min-h-0 mt-0 -mx-6 -mb-6">
          <AdminRampPage />
        </TabsContent>

        <TabsContent value="agent" className="flex-1 min-h-0 mt-0">
          <UsageDashboard monthOffset={monthOffset} setMonthOffset={setMonthOffset} />
        </TabsContent>

        <TabsContent value="fal" className="flex-1 min-h-0 mt-0">
          <FalDashboard />
        </TabsContent>

        <TabsContent value="unit-economics" className="flex-1 min-h-0 mt-0">
          <UnitEconomicsDashboard />
        </TabsContent>

        <TabsContent value="paid-attribution" className="flex-1 min-h-0 mt-0 -mx-6 -mb-6">
          <PaidAttributionPage />
        </TabsContent>

        <TabsContent value="organic-attribution" className="flex-1 min-h-0 mt-0 -mx-6 -mb-6">
          <OrganicAttributionPage />
        </TabsContent>

        <TabsContent value="investors" className="flex-1 min-h-0 mt-0 -mx-6 -mb-6">
          <AdminInvestorsPage />
        </TabsContent>
      </Tabs>
    </div>
  )
}
